import React, {
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
  } from "react";
  import { createPortal } from "react-dom";
  
  /**
   * CRMStockReport
   *
   * Fetches: `${baseUrl}/api/CRMStockReport/${companyCode}/STK_LOC_QTY_COST_ASON/${endDate}/-/-/-/-/All/${locationParameter}/${page}/1000`
   *
   * Assumptions (adjust to match your backend):
   * 1. Response JSON is either a plain array of row objects, or an object like
   *    { data: [...], totalRecords / totalPages } — both are handled below.
   * 2. Company code is read from localStorage under the key "company_code".
   * 3. Page size is fixed at 1000 (per the URL), so pagination just increments
   *    the page-number segment.
   * 4. Numeric-looking fields (qty, cost, value, price, amount, balance...) are
   *    right-aligned and shown in a tabular/mono font; everything else is left-aligned.
   *
   * Props let you override the API base URL and initial filters without editing code.
   */
  
  const DEFAULT_BASE_URL = "";
  
  // Fixed column schema, matched to the real API response shape:
  // CODE, DESCRIPTION, QTY, GROUP, SUBGROUP, CATEGORY, SUBCATEG, OEM, COST, LOCBIN, TOTAL
  const COLUMNS = [
    { key: "CODE", label: "Item Code", align: "left", mono: true },
    { key: "DESCRIPTION", label: "Description", align: "left", wrap: true },
    { key: "GROUP", label: "Group", align: "left" },
    { key: "SUBGROUP", label: "Subgroup", align: "left" },
    { key: "CATEGORY", label: "Category", align: "left" },
    { key: "SUBCATEG", label: "Subcategory", align: "left" },
    { key: "OEM", label: "OEM", align: "left" },
    { key: "LOCBIN", label: "Loc/Bin", align: "left", mono: true },
    { key: "QTY", label: "Qty", align: "right", mono: true, decimals: 2 },
    {
      key: "COST",
      label: "Unit Cost",
      align: "right",
      mono: true,
      decimals: 3,
      currency: true,
    },
    {
      key: "TOTAL",
      label: "Total Value",
      align: "right",
      mono: true,
      decimals: 5,
      currency: true,
    },
  ];
  
  function formatCellValue(value, col) {
    if (value === null || value === undefined || value === "") return "—";
  
    if (
      typeof value === "number" ||
      (col.decimals !== undefined && !isNaN(Number(value)))
    ) {
      const n = Number(value);
      const formatted = n.toLocaleString(undefined, {
        minimumFractionDigits: col.decimals ?? 0,
        maximumFractionDigits: col.decimals ?? 2,
      });
      return col.currency ? formatted : formatted;
    }
    return String(value);
  }
  
  function todayISO() {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }
  
  export default function StockReportLocationwise({
    baseUrl,
    apiLocationList,
    company_code_from_local_storage,
  }) {
    const [companyCode, setCompanyCode] = useState("");
    const [endDate, setEndDate] = useState(todayISO());
    const [locationParameter, setLocationParameter] = useState(
      apiLocationList ? apiLocationList[0]?.loc_code : "-"
    );
    const [appliedFilters, setAppliedFilters] = useState({
      endDate: todayISO(),
      locationParameter: apiLocationList ? apiLocationList[0]?.loc_code : "-",
    });
  
    const [page, setPage] = useState(1);
    const [rows, setRows] = useState([]);
    const [totalPages, setTotalPages] = useState(null); // null = unknown, infer from result size
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const [locationOpen, setLocationOpen] = useState(false);
    const [locationPos, setLocationPos] = useState({ top: 0, left: 0, width: 0 });
    const locationBtnRef = useRef(null);
    const locationListRef = useRef(null);
  
    const openLocationDropdown = () => {
      const rect = locationBtnRef.current?.getBoundingClientRect();
      if (rect) {
        setLocationPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
      }
      setLocationOpen((o) => !o);
    };
  
    useEffect(() => {
      function handleClickOutside(e) {
        const inButton = locationBtnRef.current?.contains(e.target);
        const inList = locationListRef.current?.contains(e.target);
        if (!inButton && !inList) setLocationOpen(false);
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);
  
    useEffect(() => {
      if (!locationOpen) return;
      function reposition() {
        const rect = locationBtnRef.current?.getBoundingClientRect();
        if (rect) {
          setLocationPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
        }
      }
      window.addEventListener("scroll", reposition, true);
      window.addEventListener("resize", reposition);
      return () => {
        window.removeEventListener("scroll", reposition, true);
        window.removeEventListener("resize", reposition);
      };
    }, [locationOpen]);
  
    useEffect(() => {
      try {
        setCompanyCode(company_code_from_local_storage);
      } catch (e) {
        // localStorage unavailable — leave blank, user can be prompted upstream
      }
    }, []);
  
    const buildUrl = useCallback(
      (pageNumber) => {
        const loc = appliedFilters.locationParameter || "-";
        return `${baseUrl}/api/CRMStockReport/${encodeURIComponent(
          companyCode
        )}/STK_LOC_QTY_COST_ASON/${encodeURIComponent(
          appliedFilters.endDate
        )}/-/-/-/-/All/${encodeURIComponent(loc)}/${pageNumber}/1000`;
      },
      [baseUrl, companyCode, appliedFilters]
    );
  
    const fetchReport = useCallback(
      async (pageNumber) => {
        if (!companyCode) {
          setError("No company code found in local storage.");
          setRows([]);
          return;
        }
        setLoading(true);
        setError(null);
        try {
          const res = await fetch(buildUrl(pageNumber));
          if (!res.ok) {
            throw new Error(`Request failed with status ${res.status}`);
          }
          const json = await res.json();
  
          let data = [];
          let inferredTotalPages = null;
  
          if (Array.isArray(json)) {
            data = json;
          } else if (json && Array.isArray(json.data)) {
            data = json.data;
            if (json.totalPages) inferredTotalPages = Number(json.totalPages);
            else if (json.totalRecords) {
              inferredTotalPages = Math.max(
                1,
                Math.ceil(Number(json.totalRecords) / 1000)
              );
            }
          } else if (json && Array.isArray(json.result)) {
            data = json.result;
          } else if (json && typeof json === "object") {
            // fallback: single object result, wrap it
            data = [json];
          }
  
          setRows(data);
          setTotalPages(inferredTotalPages);
        } catch (err) {
          setError(err.message || "Failed to load stock report.");
          setRows([]);
        } finally {
          setLoading(false);
        }
      },
      [buildUrl, companyCode]
    );
  
    useEffect(() => {
      fetchReport(page);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, appliedFilters, companyCode]);
  
    const handleApplyFilters = (e) => {
      setPage(1);
      setAppliedFilters({ endDate, locationParameter: locationParameter || "-" });
    };
  
    const filteredRows = useMemo(() => {
      if (!search.trim()) return rows;
      const q = search.trim().toLowerCase();
      return rows.filter((row) =>
        Object.values(row).some((v) =>
          String(v ?? "")
            .toLowerCase()
            .includes(q)
        )
      );
    }, [rows, search]);
  
    const hasNextPage = totalPages ? page < totalPages : rows.length === 1000;
    const hasPrevPage = page > 1;
  
    return (
      <div
        style={{
          "--ink": "#152238",
          "--paper": "#F5F6F8",
          "--surface": "#FFFFFF",
          "--line": "#DCE1E8",
          "--accent": "#0F6E5F",
          "--accent-soft": "#E4F2EE",
          "--muted": "#5B6472",
        }}
        className="w-full  text-[var(--ink)] font-sans"
      >
        <style>{`
          .mono { font-family: "IBM Plex Mono", "SFMono-Regular", Menlo, Consolas, monospace; font-variant-numeric: tabular-nums; }
          .stock-table th, .stock-table td { border-bottom: 1px solid var(--line); }
          .stock-table tbody tr:hover { background: var(--accent-soft); }
          .stock-table tbody tr:nth-child(even) { background: rgba(21,34,56,0.015); }
        `}</style>
  
        <div className="max-w-7xl mx-auto px-2 pt-4 pb-2">
          <header className="flex items-baseline justify-between mb-1 pb-1  border-[var(--line)]">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">
                Stock Locationwise{" "}
              </p>
            </div>
          </header>
          {/* outer div */}
          <div className="mb-6 bg-[var(--surface)] border border-[var(--line)] rounded-lg p-4 ">
            <div className="flex flex-col sm:flex-row sm:flex-nowrap items-stretch sm:items-end gap-3 sm:gap-4 mb-6 bg-[var(--surface)] border border-[var(--line)] rounded-lg p-3 sm:p-4 sm:overflow-x-auto">
              <div className="flex flex-row flex-nowrap items-end gap-2 sm:contents">
                <div className="flex flex-col gap-1 flex-1 min-w-0 sm:flex-none sm:shrink-0">
                  <label
                    className="text-xs font-medium text-[var(--muted)]"
                    htmlFor="endDate"
                  >
                    As-on date
                  </label>
                  <input
                    id="endDate"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full min-w-0 sm:w-auto border border-[var(--line)] rounded-md px-2 py-1.5 sm:px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                  />
                </div>

                <div className="flex flex-col gap-1 shrink-0">
                  <label
                    className="text-xs font-medium text-[var(--muted)]"
                    htmlFor="location"
                  >
                    Location
                  </label>
                  <button
                    id="location"
                    ref={locationBtnRef}
                    type="button"
                    onClick={openLocationDropdown}
                    className="flex items-center justify-between gap-2 border border-[var(--line)] rounded-md px-2 py-1.5 sm:px-3 text-sm bg-white w-16 sm:w-20 focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                  >
                    <span className="truncate">{locationParameter || "-"}</span>
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      className={`shrink-0 transition-transform ${
                        locationOpen ? "rotate-180" : ""
                      }`}
                    >
                      <path
                        d="M2.5 4.5L6 8l3.5-3.5"
                        stroke="var(--muted)"
                        strokeWidth="1.4"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  {locationOpen &&
                    createPortal(
                      <ul
                        ref={locationListRef}
                        role="listbox"
                        style={{
                          position: "fixed",
                          top: locationPos.top,
                          left: locationPos.left,
                          width: Math.max(locationPos.width, 256),
                        }}
                        className="max-h-64 overflow-y-auto bg-white border border-[var(--line)] rounded-md shadow-lg z-50 py-1"
                      >
                        {(apiLocationList || []).map((loc) => (
                          <li
                            key={loc.loc_code}
                            role="option"
                            aria-selected={locationParameter === loc.loc_code}
                            onClick={() => {
                              setLocationParameter(loc.loc_code);
                              setLocationOpen(false);
                            }}
                            className={`px-3 py-1.5 text-sm cursor-pointer hover:bg-[var(--accent-soft)] ${
                              locationParameter === loc.loc_code
                                ? "bg-[var(--accent-soft)] font-medium"
                                : ""
                            }`}
                          >
                            {loc.loc_code} — {loc.DESC}
                          </li>
                        ))}
                      </ul>,
                      document.body
                    )}
                </div>

                <button
                  onClick={handleApplyFilters}
                  type="submit"
                  className="shrink-0 bg-[var(--ink)] text-white text-sm font-medium px-3 sm:px-4 py-1.5 rounded-md hover:opacity-90 transition"
                >
                  Apply
                </button>
              </div>

              <div className="flex flex-col gap-1 sm:ml-auto w-full sm:w-auto sm:min-w-[200px] sm:shrink-0">
                <label
                  className="text-xs font-medium text-[var(--muted)]"
                  htmlFor="search"
                >
                  Filter results
                </label>
                <input
                  id="search"
                  type="text"
                  placeholder="Search current page…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="border border-[var(--line)] rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                />
              </div>
            </div>
  
            <div className="bg-[var(--surface)] border border-[var(--line)] rounded-lg overflow-hidden">
              <div className="overflow-auto" style={{ maxHeight: "220px" }}>
                <table className="stock-table w-full text-sm">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-[var(--ink)] text-white">
                      {COLUMNS.map((col) => (
                        <th
                          key={col.key}
                          className={`px-4 py-3 font-medium whitespace-nowrap ${
                            col.align === "right" ? "text-right" : "text-left"
                          }`}
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr>
                        <td
                          colSpan={COLUMNS.length}
                          className="px-4 py-10 text-center text-[var(--muted)]"
                        >
                          Loading stock report…
                        </td>
                      </tr>
                    )}
  
                    {!loading && !error && filteredRows.length === 0 && (
                      <tr>
                        <td
                          colSpan={COLUMNS.length}
                          className="px-4 py-10 text-center text-[var(--muted)]"
                        >
                          No stock records for this date and location.
                        </td>
                      </tr>
                    )}
  
                    {!loading &&
                      filteredRows.map((row, i) => (
                        <tr key={row.CODE ? `${row.CODE}-${i}` : i}>
                          {COLUMNS.map((col) => (
                            <td
                              key={col.key}
                              className={`px-4 py-2.5 ${
                                col.align === "right" ? "text-right" : "text-left"
                              } ${col.mono ? "mono" : ""} ${
                                col.wrap
                                  ? "whitespace-normal min-w-[260px]"
                                  : "whitespace-nowrap"
                              }`}
                            >
                              {formatCellValue(row[col.key], col)}
                            </td>
                          ))}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
  
              <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--line)] bg-[var(--paper)]">
                <span className="text-xs text-[var(--muted)]">
                  Page {page}
                  {totalPages ? ` of ${totalPages}` : ""} · {filteredRows.length}{" "}
                  row{filteredRows.length === 1 ? "" : "s"} shown
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={!hasPrevPage || loading}
                    className="text-sm px-3 py-1.5 rounded-md border border-[var(--line)] bg-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[var(--accent-soft)] transition"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={!hasNextPage || loading}
                    className="text-sm px-3 py-1.5 rounded-md border border-[var(--line)] bg-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[var(--accent-soft)] transition"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
          {error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-3">
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }